/**
 * Home Feed API Client
 *
 * API Endpoints:
 * - GET /home/banners → Banner[]
 * - GET /home/featured-videos?page=N → PaginatedResponse<Video> | Video[]
 * - GET /home/recommended-videos?page=N → PaginatedResponse<Video> | Video[]
 * - GET /home/short-videos?page=N → PaginatedResponse<Video> | Video[]
 * - GET /v2/home/short-videos?page=N → PaginatedResponse<Video> | Video[]
 * - GET /home/series?page=N → PaginatedResponse<Series> | Series[]
 * - GET /home/courses → Course[]
 * - GET /creators?page=N → PaginatedResponse<Creator> | Creator[]
 */

import type { PaginatedResponse } from '@/types/api';
import type { Banner, Course, Creator, Series, Video } from '../types';
import { apiClient } from './base-client';

const extractArray = <T>(data: unknown, key?: string): T[] => {
  if (key && typeof data === 'object' && data !== null && key in data) {
    const value = (data as Record<string, unknown>)[key];
    if (Array.isArray(value)) return value as T[];
    if (typeof value === 'object' && value !== null && 'data' in value) {
      const nested = (value as { data?: unknown }).data;
      if (Array.isArray(nested)) return nested as T[];
    }
  }
  if (Array.isArray(data)) return data as T[];
  if (typeof data === 'object' && data !== null && 'data' in data) {
    const nested = (data as { data?: unknown }).data;
    if (Array.isArray(nested)) return nested as T[];
  }
  return [];
};

function isPaginatedResponse<T>(data: unknown): data is PaginatedResponse<T> {
  return (
    typeof data === 'object' &&
    data !== null &&
    'current_page' in data &&
    'last_page' in data &&
    'data' in data &&
    Array.isArray((data as Record<string, unknown>).data)
  );
}

/**
 * Create a synthetic PaginatedResponse for endpoints that return plain arrays.
 * If the array is non-empty, assume there might be a next page.
 * If empty, mark as last page.
 */
function syntheticPaginatedResponse<T>(items: T[], page: number): PaginatedResponse<T> {
  return {
    current_page: page,
    data: items,
    first_page_url: '',
    from: items.length > 0 ? 1 : null,
    last_page: items.length > 0 ? page + 1 : page,
    last_page_url: '',
    links: [],
    next_page_url: items.length > 0 ? 'synthetic' : null,
    path: '',
    per_page: items.length,
    prev_page_url: page > 1 ? 'synthetic' : null,
    to: items.length > 0 ? items.length : null,
    total: 0,
  };
}

/**
 * Extract a PaginatedResponse from various API response shapes.
 * If the response already has pagination metadata, use it directly.
 * Otherwise, wrap the extracted array in a synthetic PaginatedResponse.
 */
function extractPaginated<T>(data: unknown, key: string, page: number): PaginatedResponse<T> {
  if (key && typeof data === 'object' && data !== null && key in data) {
    const value = (data as Record<string, unknown>)[key];
    if (isPaginatedResponse<T>(value)) return value;
  }
  if (isPaginatedResponse<T>(data)) return data;
  const items = extractArray<T>(data, key);
  return syntheticPaginatedResponse(items, page);
}

export const homeClient = {
  getBanners: async (serverToken?: string): Promise<Banner[]> => {
    const data = await apiClient.get<{ banners?: Banner[]; data?: Banner[] }>(
      '/home/banners',
      serverToken ? { serverToken } : undefined
    );
    return extractArray<Banner>(data, 'banners');
  },

  getFeaturedVideos: async (serverToken?: string, page = 1): Promise<PaginatedResponse<Video>> => {
    const data = await apiClient.get<unknown>('/home/featured-videos', {
      params: { page },
      ...(serverToken ? { serverToken } : {}),
    });
    return extractPaginated<Video>(data, 'videos', page);
  },

  getShortVideos: async (serverToken?: string, page = 1): Promise<PaginatedResponse<Video>> => {
    const data = await apiClient.get<unknown>('/home/short-videos', {
      params: { page },
      ...(serverToken ? { serverToken } : {}),
    });
    return extractPaginated<Video>(data, 'videos', page);
  },

  getShortVideosV2: async (serverToken?: string, page = 1): Promise<PaginatedResponse<Video>> => {
    const data = await apiClient.get<unknown>('/v2/home/short-videos', {
      params: { page },
      ...(serverToken ? { serverToken } : {}),
    });
    return extractPaginated<Video>(data, 'videos', page);
  },

  getRecommendedVideos: async (
    serverToken?: string,
    page = 1
  ): Promise<PaginatedResponse<Video>> => {
    const data = await apiClient.get<unknown>('/home/recommended-videos', {
      params: { page },
      ...(serverToken ? { serverToken } : {}),
    });
    return extractPaginated<Video>(data, 'videos', page);
  },

  getSeries: async (serverToken?: string, page = 1): Promise<PaginatedResponse<Series>> => {
    const data = await apiClient.get<unknown>('/home/series', {
      params: { page },
      ...(serverToken ? { serverToken } : {}),
    });
    return extractPaginated<Series>(data, 'series', page);
  },

  getCourses: async (serverToken?: string): Promise<Course[]> => {
    const data = await apiClient.get<{ courses?: Course[]; data?: Course[] }>(
      '/home/courses',
      serverToken ? { serverToken } : undefined
    );
    return extractArray<Course>(data, 'courses');
  },

  getCreators: async (serverToken?: string, page = 1): Promise<PaginatedResponse<Creator>> => {
    const data = await apiClient.get<unknown>('/creators', {
      params: { per_page: 20, page },
      ...(serverToken ? { serverToken } : {}),
    });

    // Handle the nested { creators: { data: Creator[] } } shape
    if (
      typeof data === 'object' &&
      data !== null &&
      'creators' in data &&
      typeof (data as Record<string, unknown>).creators === 'object'
    ) {
      const creatorsObj = (data as { creators: unknown }).creators;
      if (isPaginatedResponse<Creator>(creatorsObj)) return creatorsObj;
    }

    return extractPaginated<Creator>(data, 'creators', page);
  },
};
